import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useListChecklistItemsQuery,
  useListChecklistRunsQuery,
  useGetOrCreateChecklistRunMutation,
  useAnswerChecklistItemsMutation,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useScopedPath } from '../../components/ScopedLink';
import { useToast } from '../../components/Toast';
import { dublinDateString, dublinHour } from '../../utils/date';
import { ChecklistItemRow, ItemDraft } from '../../components/checklists/ChecklistItemRow';
import {
  ChecklistAnswerValue,
  ChecklistItem,
  ChecklistItemSnapshot,
  ChecklistKind,
  ClientChecklistRun,
} from '../../types';

export const KIND_LABELS: Record<ChecklistKind, string> = {
  [ChecklistKind.OPENING]: 'Opening',
  [ChecklistKind.CLOSING]: 'Closing',
};

/** Before this Dublin hour the Opening tab opens by default; from it, Closing. */
export const CLOSING_TAB_FROM_HOUR = 13;

type DraftMap = Record<string, ItemDraft>;
type RunsByKind = Partial<Record<ChecklistKind, ClientChecklistRun>>;

const KINDS: ChecklistKind[] = [ChecklistKind.OPENING, ChecklistKind.CLOSING];

/**
 * Staff view: today's OPENING and CLOSING checklists as two tabs, each with
 * its own note for the manager and its own Save. Nothing here blocks
 * navigation — this is a daily prompt, not a compliance gate. Not a
 * statutory record: no Tusla/HSA claim anywhere on this page.
 *
 * Why tabs: stacked, staff at 08:00 saw a Closing list they could not do yet
 * and at 18:00 an Opening list they had already done, under one Save that
 * read as one form. Each tab is now one checklist with one Save.
 *
 * Why one note: the explanation for a No used to be a per-item field that
 * nobody could read back. The note is the single place to write ("hand soap
 * running low", "boiler tripped, hence the No"), and it is required whenever
 * an item is answered No.
 *
 * `embedded` = rendered inside the Checklists tabbed wrapper: the wrapper
 * supplies the page frame, so drop this page's own outer padding.
 */
const DailyChecklistPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;
  const { isAdmin, isStaffOrAbove } = useAuth();
  const scopedPath = useScopedPath();
  const templateHref = scopedPath('/checklists/template');

  // Checklists are a staff-and-above surface (the backend gates every read on
  // `checklist:read-staff`). A PARENT who lands here would fire admin/staff
  // queries that 403 and see a misleading "Failed to load" — so guard before
  // any query is fired, and skip the queries for them entirely.
  const { data, isLoading, isError, refetch } = useListChecklistItemsQuery(locationId ?? '', {
    skip: !locationId || !isStaffOrAbove,
  });

  const today = useMemo(() => dublinDateString(), []);
  // A CLOSING run started before 04:00 Dublin is filed under YESTERDAY's
  // operating day (src/daily-checklists/utils/operating-day.ts), while
  // OPENING never shifts. A 2-day window (yesterday + today) is always wide
  // enough to contain both runs that are "current" right now.
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 1);
    return dublinDateString(d);
  }, []);

  const {
    data: runsData,
    isLoading: isLoadingRuns,
    isError: isRunsError,
    refetch: refetchRuns,
  } = useListChecklistRunsQuery(
    { locationId: locationId ?? '', from: yesterday, to: today },
    { skip: !locationId || !isStaffOrAbove }
  );

  const [activeKind, setActiveKind] = useState<ChecklistKind>(() =>
    dublinHour() < CLOSING_TAB_FROM_HOUR ? ChecklistKind.OPENING : ChecklistKind.CLOSING
  );

  const [runs, setRuns] = useState<RunsByKind>({});
  // A same-render-cycle mirror of `runs`, so `ensureRun` never re-derives a
  // stale "does this kind already have a run" answer (setRuns only lands on
  // the next render; a fast double-tick could otherwise race two creates).
  const runsRef = useRef<RunsByKind>({});
  // Drafts are held per kind so switching tabs never discards unsaved work
  // on the other tab; the inactive tab shows a dot while it has any.
  const [drafts, setDrafts] = useState<Record<ChecklistKind, DraftMap>>({
    [ChecklistKind.OPENING]: {},
    [ChecklistKind.CLOSING]: {},
  });
  // `undefined` = untouched (show the saved note); a string = an edit.
  const [noteDrafts, setNoteDrafts] = useState<Partial<Record<ChecklistKind, string>>>({});
  const [getOrCreateRun] = useGetOrCreateChecklistRunMutation();
  const [answerItems, { isLoading: isSaving }] = useAnswerChecklistItemsMutation();
  const { showError, showSuccess } = useToast();

  // Seed `runs` from whatever the server already has for the current
  // operating day. Per kind, the run with the latest `date` in the window is
  // the current one (at most one run per location/date/kind).
  useEffect(() => {
    if (!runsData?.runs) return;
    const latestByKind: RunsByKind = {};
    for (const run of runsData.runs) {
      const current = latestByKind[run.kind];
      if (!current || run.date > current.date) latestByKind[run.kind] = run;
    }
    runsRef.current = latestByKind;
    setRuns(latestByKind);
  }, [runsData]);

  const ensureRun = async (kind: ChecklistKind): Promise<ClientChecklistRun> => {
    const existing = runsRef.current[kind];
    if (existing) return existing;
    const { run: created } = await getOrCreateRun({ locationId: locationId!, kind }).unwrap();
    runsRef.current = { ...runsRef.current, [kind]: created };
    setRuns(runsRef.current);
    return created;
  };

  const handleDraftChange = async (kind: ChecklistKind, itemId: string, draft: ItemDraft) => {
    setDrafts((prev) => ({ ...prev, [kind]: { ...prev[kind], [itemId]: draft } }));
    // Ticking the FIRST item lazily creates that kind's run so a reload
    // mid-list doesn't lose the fact that this run has started.
    if (!runs[kind]) {
      try {
        await ensureRun(kind);
      } catch (err) {
        showError((err as any)?.data?.error || 'Failed to start the checklist');
      }
    }
  };

  const items = data?.items ?? [];
  const itemsByKind: Record<ChecklistKind, ChecklistItem[]> = {
    [ChecklistKind.OPENING]: items.filter((i) => i.kind === ChecklistKind.OPENING),
    [ChecklistKind.CLOSING]: items.filter((i) => i.kind === ChecklistKind.CLOSING),
  };

  /** Everything the tab bar and the active panel need, derived per kind. */
  const view = (kind: ChecklistKind) => {
    const run = runs[kind];
    const kindDrafts = drafts[kind];
    const savedByItemId = new Map(run?.answers.map((a) => [a.itemId, a]) ?? []);
    // Prefer the run's own item snapshot once it exists (frozen at creation);
    // fall back to the live template before any run has been started.
    const rowItems: ChecklistItemSnapshot[] = run?.items?.length
      ? run.items
      : itemsByKind[kind].map((i) => ({
          itemId: i.itemId,
          label: i.label,
          order: i.order,
          isRequired: i.isRequired,
        }));
    const pending = Object.entries(kindDrafts).filter(([, d]) => d.value !== undefined);
    const savedNote = run?.note ?? '';
    const noteDraft = noteDrafts[kind];
    const note = noteDraft ?? savedNote;
    const noteChanged = noteDraft !== undefined && noteDraft.trim() !== savedNote;
    // Effective value per item = draft over saved. Any No without a legacy
    // per-item comment needs the note — the same rule the backend enforces.
    // Considers every stored answer, not just the rows on screen, so a No
    // on an item since removed from the template still holds the note.
    const itemIds = new Set<string>([
      ...rowItems.map((i) => i.itemId),
      ...(run?.answers.map((a) => a.itemId) ?? []),
    ]);
    const noteRequired = Array.from(itemIds).some((itemId) => {
      const saved = savedByItemId.get(itemId);
      const value = kindDrafts[itemId]?.value ?? saved?.value;
      return value === ChecklistAnswerValue.NO && !saved?.comment;
    });
    const requiredRemaining = rowItems.filter(
      (i) =>
        i.isRequired && !savedByItemId.has(i.itemId) && kindDrafts[i.itemId]?.value === undefined
    ).length;
    const signedOff = run?.status === 'SIGNED_OFF';
    const hasUnsaved = pending.length > 0 || noteChanged;
    const canSave = hasUnsaved && !(noteRequired && !note.trim()) && !isSaving && !signedOff;
    return {
      run,
      rowItems,
      savedByItemId,
      pending,
      note,
      noteChanged,
      noteRequired,
      requiredRemaining,
      signedOff,
      hasUnsaved,
      canSave,
    };
  };

  const handleSave = async (kind: ChecklistKind) => {
    const v = view(kind);
    // A re-answered item carries its legacy per-item comment forward: the
    // backend replaces the whole answer on merge, so leaving it out would
    // silently erase the explanation an older run recorded for that No.
    const answers = v.pending.map(([itemId, d]) => {
      const legacyComment = v.savedByItemId.get(itemId)?.comment;
      return {
        itemId,
        value: d.value as ChecklistAnswerValue,
        ...(legacyComment ? { comment: legacyComment } : {}),
      };
    });
    try {
      const activeRun = await ensureRun(kind);
      const { run: updated } = await answerItems({
        runId: activeRun.runId,
        answers,
        ...(v.noteChanged ? { note: v.note.trim() } : {}),
      }).unwrap();
      runsRef.current = { ...runsRef.current, [kind]: updated };
      setRuns(runsRef.current);
      setDrafts((prev) => ({ ...prev, [kind]: {} }));
      setNoteDrafts((prev) => ({ ...prev, [kind]: undefined }));
      showSuccess(`${KIND_LABELS[kind]} saved.`);
    } catch (err) {
      showError((err as any)?.data?.error || `Failed to save ${KIND_LABELS[kind]}`);
    }
  };

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  // Parents (and anyone below STAFF) have no access to daily checklists.
  if (!isStaffOrAbove) {
    return (
      <div
        data-testid="daily-checklist-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (isLoading || isLoadingRuns) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError || isRunsError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Failed to load checklists.</p>
        <button
          type="button"
          data-testid="checklist-retry"
          onClick={() => {
            refetch();
            refetchRuns();
          }}
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const active = view(activeKind);

  return (
    <div
      data-testid="daily-checklist-page"
      className={embedded ? 'space-y-4 py-6' : 'max-w-3xl mx-auto px-4 py-8 space-y-4'}
    >
      {/* Opening | Closing — one checklist per tab, one Save per checklist. */}
      <div className="flex items-center gap-2" role="tablist" aria-label="Checklist">
        {KINDS.map((kind) => {
          const v = view(kind);
          const isActive = kind === activeKind;
          return (
            <button
              key={kind}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`checklist-tab-${kind}`}
              onClick={() => setActiveKind(kind)}
              className={`inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium ${
                isActive
                  ? 'border-primary-600 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {KIND_LABELS[kind]}
              {v.requiredRemaining > 0 && !v.signedOff && (
                <span className="text-xs font-medium opacity-80">{v.requiredRemaining} left</span>
              )}
              {v.hasUnsaved && !isActive && (
                <span
                  data-testid={`checklist-tab-unsaved-${kind}`}
                  title="Unsaved changes"
                  className="h-2 w-2 rounded-full bg-amber-500"
                />
              )}
            </button>
          );
        })}
      </div>

      {active.rowItems.length === 0 ? (
        <section
          data-testid={`checklist-section-${activeKind}`}
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-gray-800">{KIND_LABELS[activeKind]}</h2>
          <p className="mt-2 text-sm text-gray-500">
            No {KIND_LABELS[activeKind].toLowerCase()} items yet.
            {isAdmin && (
              <>
                {' '}
                <Link
                  to={templateHref}
                  data-testid={`checklist-manage-items-link-${activeKind}`}
                  className="text-primary-600 hover:underline"
                >
                  Add checklist items
                </Link>
                .
              </>
            )}
          </p>
        </section>
      ) : (
        <section
          data-testid={`checklist-section-${activeKind}`}
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-baseline gap-2">
              <h2 className="text-sm font-semibold text-gray-800">{KIND_LABELS[activeKind]}</h2>
              {/* The run's own server-authoritative operating day, falling back
                  to the Dublin calendar date before a run exists. */}
              <span data-testid={`checklist-date-${activeKind}`} className="text-xs text-gray-400">
                {active.run?.date ?? today}
              </span>
            </div>
            {/* Informational only — a prompt, never a gate. */}
            <span
              data-testid={`checklist-remaining-${activeKind}`}
              className="text-xs text-gray-500"
            >
              {active.signedOff
                ? `Signed off by ${active.run?.signedOffByName ?? 'a manager'}`
                : active.requiredRemaining > 0
                ? `${active.requiredRemaining} required item${
                    active.requiredRemaining === 1 ? '' : 's'
                  } left`
                : 'All required items answered'}
            </span>
          </div>

          <div className="mt-2">
            {active.rowItems.map((item) => {
              const saved = active.savedByItemId.get(item.itemId);
              return (
                <ChecklistItemRow
                  key={item.itemId}
                  item={item}
                  savedValue={saved?.value}
                  savedComment={saved?.comment}
                  draft={drafts[activeKind][item.itemId] ?? {}}
                  onChange={(itemId, draft) => handleDraftChange(activeKind, itemId, draft)}
                  disabled={isSaving || active.signedOff}
                />
              );
            })}
          </div>

          <div className="mt-3 border-t border-gray-200 pt-3">
            <label
              htmlFor={`checklist-note-${activeKind}`}
              className="block text-xs font-semibold uppercase tracking-wide text-gray-600"
            >
              Note for the manager{' '}
              <span
                data-testid={`checklist-note-hint-${activeKind}`}
                className="font-normal normal-case tracking-normal text-gray-400"
              >
                {active.noteRequired ? '(required — explain the No)' : '(optional)'}
              </span>
            </label>
            <textarea
              id={`checklist-note-${activeKind}`}
              data-testid={`checklist-note-${activeKind}`}
              rows={3}
              value={active.note}
              readOnly={active.signedOff}
              disabled={isSaving}
              onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [activeKind]: e.target.value }))}
              placeholder="Anything for the manager — supplies running low, something broken…"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 read-only:bg-gray-50 disabled:opacity-50"
            />
          </div>

          {!active.signedOff && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Saves {KIND_LABELS[activeKind].toLowerCase()} only.
              </span>
              <button
                type="button"
                data-testid={`checklist-save-${activeKind}`}
                onClick={() => handleSave(activeKind)}
                disabled={!active.canSave}
                className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : `Save ${KIND_LABELS[activeKind]}`}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default DailyChecklistPage;
